import { Controller } from "@hotwired/stimulus"

// Toast notification controller
// Provides a simple way to show toast messages from anywhere in the app
export default class extends Controller {
  static targets = ["container"]

  connect() {
    // Make showToast globally available for use from other controllers
    window.showToast = this.show.bind(this)
  }

  disconnect() {
    delete window.showToast
  }

  // Show a toast message
  // @param {string} message - The message to display
  // @param {string} type - 'success', 'error', 'warning', or 'info'
  // @param {number} duration - How long to show (ms), default 4000
  show(message, type = 'info', duration = 4000) {
    const toast = this.createToast(message, type)
    this.containerTarget.appendChild(toast)

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.remove('translate-y-2', 'opacity-0')
      toast.classList.add('translate-y-0', 'opacity-100')
    })

    // Auto-dismiss
    setTimeout(() => this.dismiss(toast), duration)
  }

  createToast(message, type) {
    const toast = document.createElement('div')

    const typeStyles = {
      success: 'bg-green-600',
      error: 'bg-red-600',
      warning: 'bg-yellow-500',
      info: 'bg-gray-800'
    }

    const icons = {
      success: `<svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>`,
      error: `<svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>`,
      warning: `<svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>`,
      info: `<svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`
    }

    toast.className = `flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white text-sm transform transition-all duration-300 translate-y-2 opacity-0 ${typeStyles[type] || typeStyles.info}`
    toast.innerHTML = `
      <span class="flex-shrink-0">${icons[type] || icons.info}</span>
      <span class="flex-1">${this.escapeHtml(message)}</span>
      <button type="button" class="flex-shrink-0 opacity-70 hover:opacity-100" data-action="click->toast#dismissEvent">
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    `

    return toast
  }

  dismissEvent(event) {
    this.dismiss(event.target.closest('[class*="bg-"]'))
  }

  dismiss(toast) {
    if (!toast) return

    toast.classList.remove('translate-y-0', 'opacity-100')
    toast.classList.add('translate-y-2', 'opacity-0')

    setTimeout(() => toast.remove(), 300)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
